import { useState, useEffect } from 'react'
import { FiPlay, FiSave, FiZap, FiPlus } from 'react-icons/fi'
import toast from 'react-hot-toast'
import api from '../utils/api'

export default function QueryEditor() {
  const [datasources, setDatasources] = useState([])
  const [queries, setQueries] = useState([])
  const [selectedDs, setSelectedDs] = useState('')
  const [sql, setSql] = useState('')
  const [queryName, setQueryName] = useState('')
  const [result, setResult] = useState(null)
  const [analysis, setAnalysis] = useState(null)
  const [loading, setLoading] = useState(false)
  const [varName, setVarName] = useState('')

  useEffect(() => {
    loadDatasources()
    loadQueries()
  }, [])

  const loadDatasources = async () => {
    try {
      const { data } = await api.get('/datasources')
      setDatasources(data)
    } catch (err) { /* ignore */ }
  }

  const loadQueries = async () => {
    try {
      const { data } = await api.get('/queries')
      setQueries(data)
    } catch (err) { /* ignore */ }
  }

  const executeQuery = async () => {
    if (!selectedDs || !sql.trim()) {
      toast.error('Veri kaynağı ve sorgu gerekli')
      return
    }
    setLoading(true)
    setAnalysis(null)
    try {
      const { data } = await api.post('/queries/execute', {
        datasource_id: parseInt(selectedDs),
        sql_text: sql,
        limit: 500,
      })
      setResult(data)
      toast.success(`${data.row_count} satır döndü`)
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sorgu hatası')
    } finally {
      setLoading(false)
    }
  }

  const analyzeQuery = async () => {
    if (!selectedDs || !sql.trim()) {
      toast.error('Veri kaynağı ve sorgu gerekli')
      return
    }
    setLoading(true)
    try {
      const { data } = await api.post('/queries/execute-and-analyze', {
        datasource_id: parseInt(selectedDs),
        sql_text: sql,
        limit: 5000,
      })
      setResult({ data: data.data, row_count: data.row_count, columns: data.analysis.columns })
      setAnalysis(data.analysis)
      toast.success('Analiz tamamlandı')
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analiz hatası')
    } finally {
      setLoading(false)
    }
  }

  const saveQuery = async () => {
    if (!queryName || !selectedDs || !sql) {
      toast.error('Ad, kaynak ve sorgu gerekli')
      return
    }
    try {
      const { data } = await api.post('/queries', {
        datasource_id: parseInt(selectedDs),
        name: queryName,
        sql_text: sql,
      })
      toast.success('Sorgu kaydedildi')
      loadQueries()
    } catch (err) {
      toast.error('Kaydetme başarısız')
    }
  }

  const createVariable = async (queryId) => {
    if (!varName) {
      toast.error('Değişken adı gerekli')
      return
    }
    try {
      await api.post('/queries/variables', {
        query_id: queryId,
        name: varName,
      })
      toast.success('Değişken oluşturuldu')
      setVarName('')
      loadQueries()
    } catch (err) {
      toast.error('Oluşturulamadı')
    }
  }

  const loadSavedQuery = (q) => {
    setSelectedDs(String(q.datasource_id))
    setSql(q.sql_text)
    setQueryName(q.name)
  }

  return (
    <div className="p-6 h-full flex flex-col">
      <h1 className="text-2xl font-bold text-gray-800 mb-1">Sorgu Editörü</h1>
      <p className="text-sm text-gray-500 mb-4">SQL sorguları yazın, çalıştırın ve otomatik analiz edin</p>

      <div className="flex-1 flex gap-4 min-h-0">
        {/* Sol - Kaydedilmiş Sorgular */}
        <div className="w-64 bg-white rounded-xl border p-3 overflow-y-auto flex-shrink-0">
          <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">Kaydedilmiş Sorgular</h3>
          {queries.map((q) => (
            <div key={q.id} className="mb-2">
              <button onClick={() => loadSavedQuery(q)} className="text-sm text-left w-full px-2 py-1.5 rounded hover:bg-primary-50 text-gray-700">
                {q.name}
              </button>
              <div className="pl-4">
                {q.variables.map((v) => (
                  <span key={v.id} className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded mr-1">{v.name}</span>
                ))}
                <div className="flex gap-1 mt-1">
                  <input
                    value={varName}
                    onChange={(e) => setVarName(e.target.value)}
                    placeholder="Değişken adı"
                    className="text-xs border rounded px-1.5 py-0.5 w-24"
                  />
                  <button onClick={() => createVariable(q.id)} className="text-xs text-primary-600 hover:text-primary-800">
                    <FiPlus size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Orta - SQL Editör */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex gap-2 mb-3 items-center">
            <select
              value={selectedDs}
              onChange={(e) => setSelectedDs(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm bg-white"
            >
              <option value="">Veri Kaynağı Seçin</option>
              {datasources.map((ds) => (
                <option key={ds.id} value={ds.id}>{ds.name} ({ds.ds_type})</option>
              ))}
            </select>
            <input
              value={queryName}
              onChange={(e) => setQueryName(e.target.value)}
              placeholder="Sorgu adı (kaydetmek için)"
              className="px-3 py-2 border rounded-lg text-sm flex-1"
            />
            <button onClick={executeQuery} disabled={loading} className="flex items-center gap-1 bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700 disabled:opacity-50">
              <FiPlay /> Çalıştır
            </button>
            <button onClick={analyzeQuery} disabled={loading} className="flex items-center gap-1 bg-purple-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50">
              <FiZap /> Otomatik Analiz
            </button>
            <button onClick={saveQuery} className="flex items-center gap-1 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-700">
              <FiSave /> Kaydet
            </button>
          </div>

          <textarea
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            className="w-full h-40 p-3 border rounded-lg font-mono text-sm bg-gray-900 text-green-400 resize-none focus:ring-2 focus:ring-primary-500 outline-none"
            placeholder="SELECT * FROM satislar WHERE tarih >= '2024-01-01'"
          />

          {/* Sonuçlar */}
          <div className="flex-1 mt-3 overflow-auto">
            {/* Analiz Özeti */}
            {analysis && (
              <div className="mb-3 bg-purple-50 border border-purple-200 rounded-xl p-4">
                <h3 className="text-sm font-medium text-purple-800 mb-2">Otomatik Analiz</h3>
                <p className="text-sm text-purple-700 mb-3">{analysis.summary}</p>

                {analysis.suggested_charts?.length > 0 && (
                  <div>
                    <span className="text-xs font-medium text-purple-600">Önerilen Grafikler:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {analysis.suggested_charts.slice(0, 6).map((s, i) => (
                        <span key={i} className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                          {s.chart_type}: {s.title}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Veri Tablosu */}
            {result && (
              <div className="bg-white rounded-xl border overflow-hidden">
                <div className="px-3 py-2 bg-gray-50 border-b text-xs text-gray-500">
                  {result.row_count} satır
                </div>
                <div className="overflow-auto max-h-96">
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        {result.columns?.map((col) => (
                          <th key={col.name} className="sticky top-0 bg-gray-50 text-left px-3 py-2 text-xs font-medium text-gray-500 border-b whitespace-nowrap">
                            {col.name}
                            <span className="ml-1 text-gray-300">({col.semantic_type || col.type})</span>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.data?.slice(0, 100).map((row, i) => (
                        <tr key={i} className="hover:bg-blue-50/50 border-b border-gray-50">
                          {result.columns?.map((col) => (
                            <td key={col.name} className="px-3 py-1.5 text-gray-700 whitespace-nowrap">
                              {row[col.name]?.toString() || ''}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
